import { newId, SAVE_DEBOUNCE_MS, type LinkSync, type Operation } from '@blueprint/domain'
import { RepositoryError, type BoardRepository, type MutationAck } from './repository.js'

export type SaveStatus = 'saved' | 'unsaved' | 'saving' | 'failed' | 'conflict'

export interface SaveState {
  status: SaveStatus
  /** Message to show beside Retry; null when there is nothing wrong. */
  error: string | null
  /** Operations not yet acknowledged, including the batch in flight. */
  pending: number
  revision: number
  /** Server revision reported by a conflict, for the reload option. */
  serverRevision: number | null
}

interface InFlight {
  mutationId: string
  expectedRevision: number
  operations: Operation[]
}

interface BoardQueue {
  revision: number
  queued: Operation[]
  inFlight: InFlight | null
  timer: ReturnType<typeof setTimeout> | null
  status: SaveStatus
  error: string | null
  serverRevision: number | null
  /** Set by a conflict or a failure; nothing is sent until Retry or Discard. */
  paused: boolean
  waiters: Array<{ resolve: () => void; reject: (error: unknown) => void }>
}

export interface SaveQueueCallbacks {
  onState: (boardId: string, state: SaveState) => void
  onAck: (boardId: string, ack: MutationAck) => void
  /** Whether a batch's label and colour changes reach the linked mindmap; read as each batch is sent. */
  sync?: () => LinkSync
}

const IDLE: Pick<BoardQueue, 'queued' | 'inFlight' | 'timer' | 'status' | 'error' | 'serverRevision' | 'paused'> = {
  queued: [],
  inFlight: null,
  timer: null,
  status: 'saved',
  error: null,
  serverRevision: null,
  paused: false,
}

/**
 * Per-board ordered save queue.
 *
 * Debounces 400 ms, allows one request in flight per board, retains operations that
 * arrive while a write is running, and never cancels a write because another edit
 * happened. A retry resends the identical mutation id and payload so a lost response
 * cannot apply the same work twice.
 */
export class SaveQueue {
  private readonly queues = new Map<string, BoardQueue>()

  constructor(
    private repository: BoardRepository,
    private readonly callbacks: SaveQueueCallbacks,
  ) {}

  /** Swap the adapter (used when the data mode changes); queues are cleared first. */
  setRepository(repository: BoardRepository): void {
    this.clear()
    this.repository = repository
  }

  private queue(boardId: string): BoardQueue {
    let queue = this.queues.get(boardId)
    if (!queue) {
      queue = { ...IDLE, queued: [], waiters: [], revision: 0 }
      this.queues.set(boardId, queue)
    }
    return queue
  }

  private emit(boardId: string, queue: BoardQueue): void {
    this.callbacks.onState(boardId, {
      status: queue.status,
      error: queue.error,
      pending: queue.queued.length + (queue.inFlight ? queue.inFlight.operations.length : 0),
      revision: queue.revision,
      serverRevision: queue.serverRevision,
    })
  }

  /** Called after hydration so the first batch carries the right expected revision. */
  track(boardId: string, revision: number): void {
    const queue = this.queue(boardId)
    Object.assign(queue, IDLE, { queued: [], revision })
    this.emit(boardId, queue)
  }

  state(boardId: string): SaveState {
    const queue = this.queue(boardId)
    return {
      status: queue.status,
      error: queue.error,
      pending: queue.queued.length + (queue.inFlight ? queue.inFlight.operations.length : 0),
      revision: queue.revision,
      serverRevision: queue.serverRevision,
    }
  }

  hasUnsavedWork(boardId?: string): boolean {
    const queues = boardId ? [this.queues.get(boardId)].filter(Boolean) : [...this.queues.values()]
    return (queues as BoardQueue[]).some((queue) => queue.queued.length > 0 || queue.inFlight !== null)
  }

  /** Operations that have not been acknowledged — offered as a JSON download on conflict. */
  unsavedOperations(boardId: string): Operation[] {
    const queue = this.queue(boardId)
    return [...(queue.inFlight?.operations ?? []), ...queue.queued]
  }

  enqueue(boardId: string, operations: Operation[]): void {
    if (operations.length === 0) return
    const queue = this.queue(boardId)
    queue.queued.push(...operations)
    if (queue.status !== 'conflict' && queue.status !== 'failed') {
      queue.status = 'unsaved'
      queue.error = null
    }
    this.emit(boardId, queue)
    this.schedule(boardId, queue)
  }

  private schedule(boardId: string, queue: BoardQueue): void {
    if (queue.paused || queue.inFlight || queue.timer !== null || queue.queued.length === 0) return
    queue.timer = setTimeout(() => {
      queue.timer = null
      void this.send(boardId, queue)
    }, SAVE_DEBOUNCE_MS)
  }

  private async send(boardId: string, queue: BoardQueue): Promise<void> {
    if (queue.paused || queue.inFlight || queue.queued.length === 0) return
    queue.inFlight = {
      mutationId: newId(),
      expectedRevision: queue.revision,
      operations: queue.queued,
      ...(this.callbacks.sync ? { sync: this.callbacks.sync() } : {}),
    }
    queue.queued = []
    queue.status = 'saving'
    queue.error = null
    this.emit(boardId, queue)
    await this.dispatch(boardId, queue)
  }

  private async dispatch(boardId: string, queue: BoardQueue): Promise<void> {
    const attempt = queue.inFlight
    if (!attempt) return
    try {
      const ack = await this.repository.applyOperations(boardId, attempt)
      // A queue cleared or retargeted while this was in flight must not be resurrected.
      if (this.queues.get(boardId) !== queue || queue.inFlight !== attempt) return
      queue.inFlight = null
      queue.revision = ack.revision
      queue.serverRevision = null
      this.callbacks.onAck(boardId, ack)
      if (queue.queued.length > 0) {
        // Already waited out a debounce; send the retained work straight away.
        this.emit(boardId, queue)
        await this.send(boardId, queue)
        return
      }
      queue.status = 'saved'
      queue.error = null
      this.emit(boardId, queue)
      this.settle(queue)
    } catch (error) {
      if (this.queues.get(boardId) !== queue || queue.inFlight !== attempt) return
      this.fail(boardId, queue, error)
    }
  }

  private fail(boardId: string, queue: BoardQueue, error: unknown): void {
    const repositoryError =
      error instanceof RepositoryError
        ? error
        : new RepositoryError('network', error instanceof Error ? error.message : 'The save could not be sent.')

    queue.paused = true
    queue.error = repositoryError.message
    if (repositoryError.kind === 'conflict') {
      queue.status = 'conflict'
      queue.serverRevision = repositoryError.currentRevision ?? null
    } else {
      queue.status = 'failed'
    }
    this.emit(boardId, queue)
    this.settle(queue, repositoryError)
  }

  private settle(queue: BoardQueue, error?: unknown): void {
    const waiters = queue.waiters
    queue.waiters = []
    for (const waiter of waiters) {
      if (error) waiter.reject(error)
      else waiter.resolve()
    }
  }

  /** Resend the identical payload. Used by the Retry action and by the failure banner. */
  retry(boardId: string): void {
    const queue = this.queue(boardId)
    queue.paused = false
    queue.error = null
    queue.serverRevision = null
    if (queue.inFlight) {
      queue.status = 'saving'
      this.emit(boardId, queue)
      void this.dispatch(boardId, queue)
      return
    }
    queue.status = queue.queued.length > 0 ? 'unsaved' : 'saved'
    this.emit(boardId, queue)
    this.schedule(boardId, queue)
  }

  /** Explicitly throw away unacknowledged work. The caller must reload the board. */
  discard(boardId: string): void {
    const queue = this.queue(boardId)
    if (queue.timer !== null) clearTimeout(queue.timer)
    Object.assign(queue, IDLE, { queued: [], revision: queue.revision })
    this.emit(boardId, queue)
    this.settle(queue)
  }

  /** Resolve once nothing is outstanding; rejects if the queue is failed or in conflict. */
  flush(boardId: string): Promise<void> {
    const queue = this.queue(boardId)
    if (queue.status === 'failed' || queue.status === 'conflict') {
      return Promise.reject(new RepositoryError('server', queue.error ?? 'There is unsaved work.'))
    }
    if (queue.queued.length === 0 && !queue.inFlight) return Promise.resolve()
    if (queue.timer !== null) {
      clearTimeout(queue.timer)
      queue.timer = null
      void this.send(boardId, queue)
    }
    return new Promise((resolve, reject) => {
      queue.waiters.push({ resolve, reject })
    })
  }

  forget(boardId: string): void {
    const queue = this.queues.get(boardId)
    if (!queue) return
    if (queue.timer !== null) clearTimeout(queue.timer)
    this.settle(queue)
    this.queues.delete(boardId)
  }

  clear(): void {
    for (const boardId of [...this.queues.keys()]) this.forget(boardId)
  }
}
